import React, { useState, useEffect, useContext } from 'react';
import TableSortLabel from '@mui/material/TableSortLabel';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  CircularProgress,
  IconButton,
  Box,
  Tooltip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import AuthContext from '../contexts/AuthContext';
//const apiBase = process.env.PORTAL_API_URL || '';
//const apiBase = process.env.PORTAL_API_URL;

function Users() {
  console.log('Environment:', process.env);
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  // State and handlers for edit role dialog
  const [editingUser, setEditingUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  // State and handlers for disable user dialog
  const [disableUser, setDisableUser] = useState(null);
  const [disableLoading, setDisableLoading] = useState(false);

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditRole(user.role);
  };

  const handleEditCancel = () => {
    setEditingUser(null);
  };

  const handleEditSave = () => {
    if (!editingUser) return;
    fetch(`${apiBase}/users/${editingUser.id}/role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens.id_token}`,
      },
      body: JSON.stringify({ role: editRole }),
    })
      .then(res => {
        if (!res.ok) {
          throw new Error('Failed to update role');
        }
        setUsers(prevUsers =>
          prevUsers.map(u =>
            u.id === editingUser.id ? { ...u, role: editRole } : u
          )
        );
        setEditingUser(null);
      })
      .catch(err => {
        console.error('Error updating user role:', err);
      });
  };
  const handleDisableClick = (user) => {
    setDisableUser(user);
  };
  const handleDisableCancel = () => {
    if (disableLoading) return;
    setDisableUser(null);
  };
  const handleDisableConfirm = () => {
    if (!disableUser) return;
    setDisableLoading(true);
    fetch(`${apiBase}/users/${disableUser.id}/role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens.id_token}`,
      },
      body: JSON.stringify({ role: 'Disabled' }),
    })
      .then(res => {
        if (!res.ok) {
          throw new Error('Failed to disable user');
        }
        setUsers(prevUsers => prevUsers.map(u => u.id === disableUser.id ? { ...u, role: 'Disabled' } : u));
        setDisableUser(null);
      })
      .catch(err => {
        console.error('Error disabling user:', err);
      })
      .finally(() => setDisableLoading(false));
  };
  const { tokens } = useContext(AuthContext);
  // Text search for name/email
  const [searchText, setSearchText] = useState('');
  // Filter by roles: include all possible user roles
  const [selectedRoles, setSelectedRoles] = useState([]);
  // Order roles so Pending Provider and Provider are adjacent, and map display labels
  const ALL_ROLES = [
    'Unassigned',
    'PendingProvider',
    'PendingOfficeStaff',
    'Personnel',
    'Patient',
    'Manager',
    'Disabled',
  ];
  const availableRoles = [
    'Unassigned',
    'PendingProvider',
    'PendingOfficeStaff',
    'Personnel',
    'Patient',
    'Manager',
    'Disabled',
  ];
  const roleLabels = {
    Unassigned: 'Unassigned',
    PendingProvider: 'Pending Provider',
    PendingOfficeStaff: 'Pending Office Staff',
    Personnel: 'Personnel',
    Patient: 'Patient',
    Manager: 'Manager',
    Disabled: 'Disabled',
  };
  // Sorting state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  // Apply sorting to users array
  const sortedUsers = users?.slice().sort((a, b) => {
    let aVal = a[orderBy] || '';
    let bVal = b[orderBy] || '';
    if (typeof aVal === 'string') {
      const cmp = aVal.localeCompare(bVal);
      return order === 'asc' ? cmp : -cmp;
    }
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  }) || [];

  useEffect(() => {
    if (!tokens?.id_token) return;
    setLoading(true);
    // Build query params for role and search filters
    let url = `${apiBase}/users`;
    const params = new URLSearchParams();
    // If no filter selected, pass all roles except Disabled
    const rolesParam = selectedRoles.length ? selectedRoles : ALL_ROLES.filter(r => r !== 'Disabled');
    if (rolesParam.length) params.append('roles', rolesParam.join(','));
    if (searchText) {
      params.append('search_text', searchText);
    }
    params.append('skip', page * rowsPerPage);
    params.append('limit', rowsPerPage);
    const query = params.toString();
    if (query) url += `?${query}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${tokens.id_token}` },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch users');
        }
        return res.json();
      })
      .then((data) => {
        setUsers(data.items || []);
        setTotal(data.total || 0);
      })
      .catch((err) => {
        console.error('Error fetching users:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [tokens, selectedRoles, searchText, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Render full Users UI with table, search/filter, and handle loading or error states inline

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Users
      </Typography>
      {/* Search and Role filter */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          variant="outlined"
          label="Search Users"
          value={searchText}
          onChange={e => { setSearchText(e.target.value); setPage(0); }}
          sx={{ minWidth: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="role-filter-label">Filter by Role</InputLabel>
          <Select
            labelId="role-filter-label"
            multiple
            value={selectedRoles}
            onChange={e => { setSelectedRoles(e.target.value); setPage(0); }}
            renderValue={(selected) => selected.map(r => roleLabels[r] || r).join(', ')}
            label="Filter by Role"
          >
            {availableRoles.map(role => (
              <MenuItem key={role} value={role}>
                <Checkbox checked={selectedRoles.indexOf(role) > -1} />
                <ListItemText primary={roleLabels[role] || role} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {/* Show error if present */}
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          Error: {error}
        </Typography>
      )}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'id' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleRequestSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'name' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'name'}
                  direction={orderBy === 'name' ? order : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'email' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleRequestSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'role' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'role'}
                  direction={orderBy === 'role' ? order : 'asc'}
                  onClick={() => handleRequestSort('role')}
                >
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.map((user) => {
              const shortId = String(user.id).split('-')[0];
              return (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Tooltip title={user.id} arrow>
                        <Typography variant="body2" sx={{ cursor: 'default' }}>
                          {shortId}
                        </Typography>
                      </Tooltip>
                      <Tooltip title="Copy full UUID" arrow>
                        <IconButton
                          aria-label="copy full uuid"
                          size="small"
                          onClick={() => navigator.clipboard.writeText(user.id)}
                          sx={{ ml: 0.5 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    <IconButton
                      aria-label="edit"
                      size="small"
                      onClick={() => handleEditClick(user)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <Tooltip title={user.role === 'Disabled' ? 'User already disabled' : 'Disable user'}>
                      <span>
                        <IconButton
                          aria-label="disable user"
                          size="small"
                          onClick={() => handleDisableClick(user)}
                          disabled={user.role === 'Disabled'}
                          sx={{ ml: 1 }}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          </Table>
        )}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Dialog open={Boolean(editingUser)} onClose={handleEditCancel}>
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent>
          <FormControl fullWidth>
            <InputLabel id="edit-role-label">Role</InputLabel>
            <Select
              labelId="edit-role-label"
              value={editRole}
              onChange={e => setEditRole(e.target.value)}
              label="Role"
            >
              {(() => {
                const baseOptions = ['Manager', 'Personnel'];
                const canSeePending = editingUser && ['Unassigned', 'PendingProvider', 'PendingOfficeStaff'].includes(editingUser.role);
                const options = canSeePending ? [...baseOptions, 'PendingProvider', 'PendingOfficeStaff'] : baseOptions;
                return options.map(opt => (
                  <MenuItem key={opt} value={opt}>{roleLabels[opt] || opt}</MenuItem>
                ));
              })()}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(disableUser)} onClose={handleDisableCancel}>
        <DialogTitle>Disable User</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to disable this user?
          </Typography>
          <Typography variant="body2">
            Name: <strong>{disableUser?.name || 'N/A'}</strong>
          </Typography>
          <Typography variant="body2">
            Email: <strong>{disableUser?.email || 'N/A'}</strong>
          </Typography>
          <Box mt={2}>
            <Typography variant="body2" color="error">
              This user will no longer be able to use the Verabind application.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDisableCancel} disabled={disableLoading}>Cancel</Button>
          <Button onClick={handleDisableConfirm} color="error" variant="contained" disabled={disableLoading}>
            {disableLoading ? 'Disabling...' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Users; 
